import React, { useState, useCallback } from 'react';
import { Form, Button } from 'antd';
import { LockFilled } from '@ant-design/icons';
import { Elements, useStripe, useElements, PaymentElement, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import i18n from '@Shared/utils/i18n';
import { useSubmitTrialFormMutation } from '@Entities/signup/api/signupSlice';
import { ga4event } from '@Shared/utils/ga4event';
import { signupEvent } from '@Shared/utils/posthog';
import useLocalStorage from '@Shared/utils/useLocalStorage';
import { PlanType } from '@Features/auth/signin/ui/TrialStep';

import { elementsOptions } from './stripeElements';

import type {
    StripeError,
    StripePaymentElementOptions,
    StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';

import './styles.less';

const ADVERTISER_PAYMENT_METHOD_COOKIE = 'advertiser_payment_method_on_signup';

// If app is running on a localhost it will not work, use signup2.dev.tap domain
function setSignupPaymentMethodCookie(method: 'credit card') {
    try {
        const rootDomain = window.location.host.split('.').slice(1).join('.');
        const expires = new Date(Date.now() + 60 * 60 * 1000).toUTCString();
        const base = `${ADVERTISER_PAYMENT_METHOD_COOKIE}=${encodeURIComponent(method)}; Expires=${expires}; Path=/`;
        const secureAndSameSite = window.location.protocol === 'https:' ? '; Secure; SameSite=Lax' : '; SameSite=Lax';
        const cookie = `${base}; Domain=.${rootDomain}${secureAndSameSite}`;
        document.cookie = cookie;
    } catch (e) {
        console.error('setSignupPaymentMethodCookie error', e);
    }
}

// A card Stripe refused, for the funnel's analytics: the error's kind only, as the full error may carry the payment
// method with card details.
function reportStripeError({ type, code, decline_code, message }: StripeError) {
    ga4event({
        event_name: 'send_advertiser_payment_method_error_f',
        event_option1: 'advertiser registration funnel',
        event_option2: 'onboarding;filled credit card',
        event_option4: JSON.stringify({ type, code, decline_code, message }),
    });
}

// Apple Pay / Google Pay buttons, in a Stripe group of their own: confirming a wallet checks the wallet only, never the
// empty card form below it. The sheet stays open until told how it went.
function WalletButtons({
    onPaymentMethod,
    onShown,
}: {
    onPaymentMethod: (paymentMethodId: string, paymentMethod: string) => void;
    onShown: (shown: boolean) => void;
}) {
    const stripe = useStripe();
    const elements = useElements();

    const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
        if (!stripe || !elements) {
            event.paymentFailed({ reason: 'fail' });
            return;
        }
        const { error: submitError } = await elements.submit();
        if (submitError) {
            event.paymentFailed({ reason: 'fail' });
            return;
        }
        const stripeRes = await stripe.createPaymentMethod({ elements });
        if (stripeRes.error) {
            reportStripeError(stripeRes.error);
            event.paymentFailed({ reason: 'fail' });
            return;
        }
        onPaymentMethod(stripeRes.paymentMethod.id, event.expressPaymentType);
    };

    // Link is not offered (Vova 29.10.2026), nor PayPal or Amazon Pay: the server takes cards.
    return (
        <ExpressCheckoutElement
            onConfirm={handleConfirm}
            onReady={({ availablePaymentMethods }) => onShown(Boolean(availablePaymentMethods))}
            options={{ buttonHeight: 52, paymentMethods: { link: 'never', paypal: 'never', amazonPay: 'never' } }}
        />
    );
}

type TrialFormDataType = {
    paymentMethodId?: string;
    paymentProvider: string;
};

export default function TrialForm({ activePlan, isPlansLoading }: { activePlan: PlanType; isPlansLoading: boolean }) {
    const { t } = useTranslation('translation', { keyPrefix: 'signup' });
    const storage = useLocalStorage();
    const navigate = useNavigate();
    const [submitTrialForm, { isLoading }] = useSubmitTrialFormMutation();
    const stripe = useStripe();
    const elements = useElements();
    const [errors, setErrors] = useState<Partial<TrialFormDataType>>({});
    const [form] = Form.useForm();
    const [stripeError, setStripeError] = useState<string | undefined>();
    // Apple Pay / Google Pay buttons appear only where the browser has them; the "or" under them, with them.
    const [walletsShown, setWalletsShown] = useState(false);
    const resetLocalStorage = useCallback(() => {
        storage.remove('currentStep');
        storage.remove('stepUuid');
        storage.remove('authStepData');
        storage.remove('companyStepData');
        storage.remove('planId');
    }, [storage]);

    const submitTrial = useCallback(
        async (body: TrialFormDataType, planId: unknown, trialProperties: Record<string, unknown>) => {
            setStripeError(undefined);
            try {
                const uuid = storage.read('stepUuid');
                const res = await submitTrialForm({
                    body: {
                        language: i18n.language,
                        ...body,
                    },
                    id: uuid,
                    planId,
                });

                if (res?.error && 'data' in res.error) {
                    setErrors((res.error.data as { errors?: Partial<TrialFormDataType> })?.errors || {});
                } else {
                    if (res?.data?.redirectLink) {
                        // The account and the trial exist now (SignUpService::register): both facts are sent here,
                        // once, not by the page that follows, so a reload or "back" there counts nothing twice.
                        // billing:trial_started belongs to the server's billing; sent from here until the server does.
                        signupEvent('signup:flow_completed', trialProperties);
                        signupEvent('billing:trial_started', trialProperties);
                        resetLocalStorage();
                        navigate('/complete/', { replace: true, state: { redirectLink: res.data.redirectLink } });
                    }
                }
            } catch (err) {
                console.error('error: ', err);
            }
        },
        [submitTrialForm, storage, resetLocalStorage, navigate],
    );

    // Stripe's form marks a wrong or missing field red itself: the form shakes, nothing is written twice.
    const [cardShaking, setCardShaking] = useState(false);
    const shakeCard = () => {
        setCardShaking(false);
        requestAnimationFrame(() => setCardShaking(true));
    };

    // The card's payment method goes to the server, which takes its id as before. Card only (Vova 29.10.2026): PayPal
    // is no longer offered at sign-up; accounts already paying with PayPal keep it. No name on card: Stripe never got
    // it and the server keeps it optional and unused.
    // The trial's facts for analytics (engineering/analytics-events.md in tapfiliate/knowledge): the plan as chosen,
    // how it was paid, and which start page the visitor came through.
    const submitPaymentMethod = (paymentMethodId: string, paymentMethod: string) => {
        setSignupPaymentMethodCookie('credit card');
        const path = window.location.pathname;
        submitTrial({ paymentMethodId, paymentProvider: 'credit-card' }, activePlan.id, {
            plan_id: activePlan.id,
            plan: activePlan.title,
            billing_cycle: activePlan.cycle,
            price: Number(activePlan.price.replace(',', '')),
            currency: activePlan.currency,
            trial_days: Number(activePlan.trialDays),
            payment_method: paymentMethod,
            entry: path.includes('blackfriday')
                ? 'blackfriday'
                : path.includes('lovely-user/winback')
                  ? 'winback'
                  : 'regular',
        });
    };

    // The button is never disabled (Vova 29.10.2026): pressed too early or with the card unfinished, it shakes the
    // form. A refusal Stripe's form does not show itself (a card Stripe will not take) is written under the form.
    const handleSubmit = async () => {
        setStripeError(undefined);
        if (!stripe || !elements || isPlansLoading || !activePlan) {
            shakeCard();
            return;
        }
        const { error: submitError } = await elements.submit();
        if (submitError) {
            shakeCard();
            return;
        }
        const stripeRes = await stripe.createPaymentMethod({ elements });
        if (stripeRes.error) {
            reportStripeError(stripeRes.error);
            setStripeError(stripeRes.error.message);
            shakeCard();
            return;
        }
        submitPaymentMethod(stripeRes.paymentMethod.id, 'card');
    };

    // The one action, full width as on steps 1 and 2, and no way back from it (Vova 29.10.2026). The card fields end
    // with a line of small print: more room above it than between fields.
    const actionButton = (
        <Button
            block
            type="primary"
            htmlType="submit"
            style={{ fontWeight: 600, marginTop: 16 }}
            size="large"
            loading={isLoading}
        >
            {t('trial_form.activate_trial')}
        </Button>
    );

    return (
        <>
            <Form
                name="trial-form"
                layout="vertical"
                form={form}
                onFinish={handleSubmit}
                requiredMark={false}
            >
                {/* Stripe's one-click buttons on top, as Apple Pay stood above the card before; no Link (Vova chose
                    them over wallets inside the card form, 29.10.2026). */}
                <Elements
                    stripe={stripe}
                    options={elementsOptions(activePlan?.currency, i18n.language)}
                >
                    <WalletButtons
                        onPaymentMethod={submitPaymentMethod}
                        onShown={setWalletsShown}
                    />
                </Elements>
                {/* Ours, not Stripe's: shown with the buttons only, so without them the card form stands alone. */}
                {walletsShown ? <div className="wallets-or">{t('trial_form.or_card')}</div> : null}
                <Form.Item
                    className={cardShaking ? 'field-shake' : undefined}
                    validateStatus={errors?.paymentMethodId || errors?.paymentProvider ? 'error' : ''}
                    help={errors?.paymentMethodId || errors?.paymentProvider || null}
                >
                    <PaymentElement
                        onChange={() => setStripeError(undefined)}
                        options={{
                            layout: 'tabs',
                            // Billing details left to Stripe: for a card, the country and, where it is used (US, UK,
                            // Canada), the postcode, which the card's bank checks; never a full address. No offer to
                            // save the card with Link (Vova 29.10.2026).
                            // Apple Pay and Google Pay are the buttons above, not tabs here.
                            wallets: {
                                applePay: 'never',
                                googlePay: 'never',
                                link: 'never',
                            } as StripePaymentElementOptions['wallets'],
                        }}
                    />
                    {stripeError ? (
                        <div
                            id="trial-form_stripe_help"
                            className="ant-form-item-explain ant-form-css-var ant-form-item-explain-connected"
                            role="alert"
                        >
                            <div className="ant-form-item-explain-error">{stripeError}</div>
                        </div>
                    ) : null}
                </Form.Item>
                <Form.Item style={{ margin: 0 }}>{actionButton}</Form.Item>
                {/* The padlock under the button, the familiar sign of a safe payment (Vova 29.10.2026). */}
                <div className="secured-by-stripe">
                    <LockFilled /> {t('trial_form.secured_by_stripe')}
                </div>
            </Form>
        </>
    );
}
